import type { LucideIcon } from 'lucide-react';
import { BarChart3, Flag, Home, Trophy, User } from 'lucide-react';

/** Tabs shown in the bottom bar (symmetric 2 · Play · 2). */
export type NavTab = 'home' | 'board' | 'events' | 'profile';

/** Full app routing — includes screens reached from Home / Profile links. */
export type AppTab = NavTab | 'food';

interface Props {
  tab: AppTab;
  roundsOpen?: boolean;
  onTab: (tab: NavTab) => void;
  onJustPlay: () => void;
}

const LEFT_TABS: { id: NavTab; Icon: LucideIcon; label: string }[] = [
  { id: 'home', Icon: Home, label: 'Home' },
  { id: 'board', Icon: BarChart3, label: 'Board' },
];

const RIGHT_TABS: { id: NavTab; Icon: LucideIcon; label: string }[] = [
  { id: 'events', Icon: Trophy, label: 'Events' },
  { id: 'profile', Icon: User, label: 'Profile' },
];

function NavItem({
  active,
  label,
  Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  Icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="bt-nav-item flex min-w-0 flex-col items-center justify-end gap-1 pb-0.5 pt-2"
      aria-current={active ? 'page' : undefined}
    >
      <span
        className={`h-0.5 w-4 rounded-full transition-all duration-200 ${
          active ? 'bg-gold opacity-100' : 'bg-transparent opacity-0'
        }`}
        aria-hidden
      />
      <Icon
        size={21}
        strokeWidth={active ? 2.4 : 1.75}
        className={active ? 'text-navy' : 'text-mute'}
      />
      <span
        className={`max-w-full truncate text-[9px] font-semibold leading-none tracking-tight ${
          active ? 'text-navy' : 'text-mute'
        }`}
      >
        {label}
      </span>
    </button>
  );
}

export function BottomNav({ tab, roundsOpen, onTab, onJustPlay }: Props) {
  const playActive = !!roundsOpen;

  return (
    <nav
      className="ui-chrome fixed bottom-0 left-0 right-0 z-40 mx-auto max-w-phone overflow-visible border-t border-line/70 bg-[rgba(255,255,255,0.92)] shadow-[0_-8px_32px_rgba(10,30,63,0.08)] backdrop-blur-2xl backdrop-saturate-150"
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 6px)',
        WebkitBackdropFilter: 'saturate(150%) blur(24px)',
      }}
      aria-label="Main navigation"
    >
      {/* 5 equal columns: Home · Board · Play · Events · Profile */}
      <div className="grid grid-cols-5 items-end gap-0 overflow-visible px-1 pb-1 pt-5">
        {LEFT_TABS.map(({ id, Icon, label }) => (
          <NavItem
            key={id}
            active={tab === id}
            label={label}
            Icon={Icon}
            onClick={() => onTab(id)}
          />
        ))}

        <div className="flex flex-col items-center justify-end overflow-visible pb-0.5">
          <button
            type="button"
            onClick={onJustPlay}
            className="bt-nav-item bt-flat -mt-6 mb-1 flex flex-col items-center gap-1 overflow-visible"
            aria-current={playActive ? 'page' : undefined}
            aria-label="Play"
          >
            <div
              className={`flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-navy transition-transform active:scale-95 ${
                playActive
                  ? 'shadow-[0_0_0_2px_#fafaf6,0_0_0_4px_#c4a662,0_8px_24px_rgba(10,30,63,0.35)]'
                  : 'shadow-[0_6px_20px_rgba(10,30,63,0.32)]'
              }`}
              style={{ color: '#fafaf6' }}
            >
              <Flag size={22} strokeWidth={2.5} aria-hidden />
            </div>
            <span
              className={`text-[9px] font-bold leading-none tracking-tight ${
                playActive ? 'text-gold-deep' : 'text-navy'
              }`}
            >
              Play
            </span>
          </button>
        </div>

        {RIGHT_TABS.map(({ id, Icon, label }) => (
          <NavItem
            key={id}
            active={tab === id}
            label={label}
            Icon={Icon}
            onClick={() => onTab(id)}
          />
        ))}
      </div>
    </nav>
  );
}
